import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table';

import style from './Users.module.css';
import { buildTestData } from 'Vulture/TestData';
import { dlAddNewUser } from 'Vulture/DataLayer';
import {
	canAdministerScope,
} from 'Vulture/Permissions';

function UserTable(props) {
	const data = React.useMemo(() => {
		return props.data;
	}, [props.data]);

	const columns = React.useMemo(() => {
		return props.columns;
	}, [props.columns]);


	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		preGlobalFilteredRows,
		prepareRow,
		rows,
		setGlobalFilter,
		state,
	} = useTable(
		{
			columns,
			data,
			initialState: {
				sortBy: [
					{
						id: 'email',
						desc: false
					}
				]
			}
		},
		useGlobalFilter,
		useSortBy
	);

	return (
		<React.Fragment>
			<div className='secondaryNav'>
				<div
					data-testing-info={buildTestData('button--addNew')}
					data-test-id='addNewButton'
					className='button'
					onClick={(e) => {
						e.preventDefault();
						dlAddNewUser();
						props.openModal({
							original: {
								accounts: [],
								active:   true,
								email:    '',
								role:     '',
								uuid:     '',
								has_2fa:  true,
							},
						});
					}}
				>
					<CustomIcon icon="plus"/>Add New
				</div>
				<div>
					<GlobalFilter
						preGlobalFilteredRows={preGlobalFilteredRows}
						globalFilter={state.globalFilter}
						setGlobalFilter={setGlobalFilter}
						handleRootUserReadRequest={props.handleRootUserReadRequest}
					/>
				</div>
			</div>
			<div className={style.table} {...getTableProps()}>
				<div className={style.thead}>
					{headerGroups.map((headerGroup, i) => {
						return (
							<div className={style.tr} key={i} {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column, i) => {
									return (
										<div className={style.th}
											key={i}
											{...column.getHeaderProps(column.getSortByToggleProps())}
											style={{
												flexDirection: 'row',
												alignItems: 'center',
												cursor: 'pointer'
											}}
										>
											<span>{column.render('Header')}</span>
											<span style={{ marginLeft: '4px' }}>
												{column.isSorted
													? column.isSortedDesc
														? '▼'
														: '▲'
													: ''}
											</span>
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
				<div className={style.tbody} {...getTableBodyProps()}>
					{rows.map((row, i) => {
						prepareRow(row);
						return (
							<div className={style.tr}
								key={i}
								{...row.getRowProps()}
								onClick={(e) => {
									e.preventDefault();
									if (props.isAccountUser) {
										// You may only open a user whose reach is narrower than your own.
										// Compares scope_type (from the user payload), not the role name —
										// a custom copy of external_admin must be as unopenable as
										// external_admin itself.
										if (!canAdministerScope(row.original.scope_type, row.original.beyond_reach)) {
											return;
										}
									}
									props.openModal(row);
								}}
								data-testing-info={buildTestData(`listItem--${row.values.email}`)}
							>
								{row.cells.map((cell, j) => {
									return (
										<div className={style.td}
											key={j}
											{...cell.getCellProps()}
										>
											<div className={style.tdContainer}>
												{cell.render('Cell')}
											</div>
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		</React.Fragment>
	);
}

function GlobalFilter({
	preGlobalFilteredRows,
	globalFilter,
	setGlobalFilter,
	handleRootUserReadRequest,
}) {
	const count = preGlobalFilteredRows.length;
	const [value, setValue] = React.useState(globalFilter);

	// TODO: get search working on all columns
	const onChange = useAsyncDebounce(value => {
		if (handleRootUserReadRequest) {
			if (value.length >= 3) {
				setGlobalFilter(value || undefined);
				handleRootUserReadRequest(value);
			}
		} else {
			setGlobalFilter(value || undefined);
		}
	}, 300);

	return (
		<div className={style.userSearch}>
			<CustomIcon icon='search' color='var(--accent)' />
			<input
				value={value || ''}
				onChange={e => {
					setValue(e.target.value);
					onChange(e.target.value);
				}}
				placeholder={`${count} records...`}
				data-testing-info={buildTestData('input--search')}
				data-test-id="userSearchInput"
			/>
		</div>
	);
}

export default UserTable;